/**
 * Morse-Ton: der Anlauf nach dem Wecken. (08.10.2026, Messung "erster Buchstabe langsam")
 *
 * Martin (Morse-Trainer, Lautsprecher): "Manchmal kommt der erste Buchstabe langsam." Der Zeitplan ist exakt
 * (tests/browser/morse-ton-zeit.mts); verdächtig ist der Anlauf der Tonausgabe: `resume()` löst auf, aber die Uhr
 * des Kontexts (`currentTime`) steht noch, und was dann auf "jetzt + 0,08 s" geplant wird, liegt schon in der
 * Vergangenheit, wenn die Uhr anläuft — der erste Ton setzt spät oder verkürzt ein.
 *
 * Darum, NUR bei kaltem Start (der Kontext schlief oder wurde gerade erst geweckt):
 *   1. ein stummer Puffer (50-100 ms) läuft durch den Ausgang und zieht die Ausgabe an;
 *   2. geplant wird erst, wenn `currentTime` nachweislich vorangeschritten ist — höchstens `MORSE_ANLAUF_FRIST_MS`,
 *      danach trotzdem (ein Ton mit etwas Verzug ist besser als keiner).
 * Der größere Vorlauf (`MORSE_VORLAUF_KALT_S`, morse-ton.ts) deckt den Rest. Warm bleibt alles wie vorher.
 *
 * ⚠️ Ob das am iPhone hilft, sagt nur die Messzeile `[morse-ton] ... anlauf=<ms>/<ok|frist>` (morse-messung.ts).
 */

/** Länge des stummen Vorspiels (Sekunden). */
const MORSE_STILLE_S = 0.08;
/** So lange wird höchstens auf die Uhr des Kontexts gewartet. */
export const MORSE_ANLAUF_FRIST_MS = 300;
/** Abstand der Blicke auf die Uhr, solange gewartet wird. */
const MORSE_ANLAUF_SCHRITT_MS = 5;

type AnlaufErgebnis = "ok" | "frist" | "abbruch";

export interface AnlaufBefund {
  /** ok: die Uhr lief an · frist: sie stand nach der Frist noch · abbruch: gestoppt, während gewartet wurde. */
  ergebnis: AnlaufErgebnis;
  /** Wie lange der Anlauf gedauert hat (ms, Uhr der Seite). */
  ms: number;
}

/** Ein stummer Puffer durch den Ausgang. Wirft nie: ohne Vorspiel geht der Ton trotzdem. */
function stillesVorspiel(c: AudioContext): void {
  try {
    const n = Math.max(1, Math.round(c.sampleRate * MORSE_STILLE_S));
    const quelle = c.createBufferSource();
    quelle.buffer = c.createBuffer(1, n, c.sampleRate);
    quelle.connect(c.destination);
    quelle.onended = () => {
      try {
        quelle.disconnect();
      } catch {
        /* schon ab */
      }
    };
    quelle.start(0);
  } catch {
    /* kein Puffer, kein Vorspiel */
  }
}

/**
 * Stilles Vorspiel starten und warten, bis die Uhr des Kontexts vorangeschritten ist (`currentTime` größer als am
 * Anfang) — höchstens `fristMs`. `abbruch`: wird bei jedem Blick gefragt (Stopp während des Wartens).
 */
export async function laufAn(
  c: AudioContext,
  o: { uhr: () => number; abbruch?: () => boolean; fristMs?: number },
): Promise<AnlaufBefund> {
  const t0 = o.uhr();
  const frist = o.fristMs ?? MORSE_ANLAUF_FRIST_MS;
  const start = c.currentTime;
  stillesVorspiel(c);
  let ergebnis: AnlaufErgebnis = "frist";
  for (;;) {
    if (c.currentTime > start) {
      ergebnis = "ok";
      break;
    }
    if (o.abbruch?.()) {
      ergebnis = "abbruch";
      break;
    }
    if (o.uhr() - t0 >= frist) break;
    await new Promise<void>((fertig) => setTimeout(fertig, MORSE_ANLAUF_SCHRITT_MS));
  }
  return { ergebnis, ms: Math.max(0, Math.round(o.uhr() - t0)) };
}
